/**
 * GPS Coordinate Utilities
 * Handles conversion between DMS (Degrees, Minutes, Seconds) and decimal formats
 */

/**
 * Parse DMS (Degrees, Minutes, Seconds) coordinate string
 * @param {string} dmsString - Format: "1°06'50.0"S 36°44'34.8"E"
 * @returns {Object} - { latitude: number, longitude: number }
 */
export function parseDMSCoordinates(dmsString) {
  try {
    // Remove extra spaces and normalize
    const normalized = dmsString.trim().replace(/\s+/g, ' ');
    
    // Split by space to separate lat and lng
    const parts = normalized.split(' ');
    
    if (parts.length !== 2) {
      throw new Error('Invalid DMS format. Expected: "1°06\'50.0"S 36°44\'34.8"E"');
    }
    
    const [latString, lngString] = parts;
    
    // Parse latitude
    const latitude = parseDMSComponent(latString);
    
    // Parse longitude
    const longitude = parseDMSComponent(lngString);
    
    // Validate coordinate ranges
    if (latitude < -90 || latitude > 90) {
      throw new Error('Latitude must be between -90 and 90 degrees');
    }
    
    if (longitude < -180 || longitude > 180) {
      throw new Error('Longitude must be between -180 and 180 degrees');
    }
    
    return { latitude, longitude };
  } catch (error) {
    throw new Error(`Failed to parse DMS coordinates: ${error.message}`);
  }
}

/**
 * Parse individual DMS component (latitude or longitude)
 * @param {string} dmsComponent - Format: "1°06'50.0"S" or "36°44'34.8"E"
 * @returns {number} - Decimal degrees
 */
function parseDMSComponent(dmsComponent) {
  // Extract direction (N, S, E, W)
  const direction = dmsComponent.slice(-1);
  const coordinate = dmsComponent.slice(0, -1);
  
  // Parse degrees, minutes, seconds
  const dmsRegex = /^(\d+)°(\d+)'([\d.]+)"$/;
  const match = coordinate.match(dmsRegex);
  
  if (!match) {
    throw new Error(`Invalid DMS component format: ${dmsComponent}`);
  }
  
  const [, degrees, minutes, seconds] = match;
  
  // Convert to decimal degrees
  let decimal = parseInt(degrees) + parseInt(minutes) / 60 + parseFloat(seconds) / 3600;
  
  // Apply direction
  if (direction === 'S' || direction === 'W') {
    decimal = decimal * -1;
  }
  
  return decimal;
}

/**
 * Convert decimal degrees to DMS format
 * @param {number} decimal - Decimal degrees
 * @param {boolean} isLatitude - True for latitude, false for longitude
 * @returns {string} - DMS format string
 */
export function decimalToDMS(decimal, isLatitude) {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutes) * 60;
  
  // Determine direction
  let direction;
  if (isLatitude) {
    direction = decimal >= 0 ? 'N' : 'S';
  } else {
    direction = decimal >= 0 ? 'E' : 'W';
  }
  
  return `${degrees}°${minutes}'${seconds.toFixed(1)}"${direction}`;
}

/**
 * Validate GPS coordinate format
 * @param {string} input - Input string to validate
 * @returns {Object} - { isValid: boolean, format: string, coordinates: Object|null, error: string|null }
 */
export function validateGPSCoordinates(input) {
  try {
    const trimmed = input.trim();
    
    // Check if it's DMS format
    if (trimmed.includes('°') && trimmed.includes("'") && trimmed.includes('"')) {
      const coordinates = parseDMSCoordinates(trimmed);
      return {
        isValid: true,
        format: 'DMS',
        coordinates,
        error: null
      };
    }
    
    // Check if it's decimal format (lat, lng)
    const decimalRegex = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
    if (decimalRegex.test(trimmed)) {
      const [latStr, lngStr] = trimmed.split(',').map(s => s.trim());
      const latitude = parseFloat(latStr);
      const longitude = parseFloat(lngStr);
      
      if (latitude < -90 || latitude > 90) {
        return {
          isValid: false,
          format: 'decimal',
          coordinates: null,
          error: 'Latitude must be between -90 and 90 degrees'
        };
      }
      
      if (longitude < -180 || longitude > 180) {
        return {
          isValid: false,
          format: 'decimal',
          coordinates: null,
          error: 'Longitude must be between -180 and 180 degrees'
        };
      }
      
      return {
        isValid: true,
        format: 'decimal',
        coordinates: { latitude, longitude },
        error: null
      };
    }
    
    return {
      isValid: false,
      format: 'unknown',
      coordinates: null,
      error: 'Invalid GPS format. Use DMS (1°06\'50.0"S 36°44\'34.8"E) or decimal (-1.1139, 36.7430)'
    };
  } catch (error) {
    return {
      isValid: false,
      format: 'unknown',
      coordinates: null,
      error: error.message
    };
  }
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param {number} lat1 - First latitude
 * @param {number} lon1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lon2 - Second longitude
 * @returns {number} - Distance in meters
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees to convert
 * @returns {number} - Radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Check if coordinates are within geofence radius
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @param {number} siteLat - Site's latitude
 * @param {number} siteLng - Site's longitude
 * @param {number} radius - Geofence radius in meters
 * @returns {Object} - { isWithinRadius: boolean, distance: number }
 */
export function checkGeofence(userLat, userLng, siteLat, siteLng, radius) {
  const distance = calculateDistance(userLat, userLng, siteLat, siteLng);
  return {
    isWithinRadius: distance <= radius,
    distance: Math.round(distance * 100) / 100 // Round to 2 decimal places
  };
}

/**
 * Get user's current GPS location
 * @returns {Promise<Object>} - { latitude: number, longitude: number, accuracy: number }
 */
export function getCurrentLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
}

/**
 * Reverse geocode coordinates to get address/location name
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 * @returns {Promise<string>} - Address or location description
 */
export async function reverseGeocode(latitude, longitude) {
  try {
    // Using OpenStreetMap Nominatim API for reverse geocoding (free, no API key required)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'FieldFlow-App'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to reverse geocode location');
    }

    const data = await response.json();
    
    // Build a readable address from the response
    const address = data.address || {};
    const parts = [];
    
    // Add relevant address components
    if (address.building || address.house_number) {
      parts.push(address.building || address.house_number);
    }
    if (address.road) {
      parts.push(address.road);
    }
    if (address.suburb || address.neighbourhood) {
      parts.push(address.suburb || address.neighbourhood);
    }
    if (address.city || address.town || address.village) {
      parts.push(address.city || address.town || address.village);
    }
    
    // If we have address parts, return them
    if (parts.length > 0) {
      return parts.join(', ');
    }
    
    // Fall back to display name or coordinates
    return data.display_name || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    // Fall back to coordinates if reverse geocoding fails
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }
}
